import React, { useEffect, useState } from 'react'
import type { TFunction } from 'i18next'
import {
  PriceEvolutionChart,
  PriceEvolutionByCategory,
} from '../../../PriceEvolutionChart'

import { useRentalMarket } from '../../RentalMarketContext'

interface RentalPriceEvolutionProps {
  t: TFunction
}

export const RentalPriceEvolution: React.FC<RentalPriceEvolutionProps> = ({
  t,
}) => {
  const { filters, service, priceMode } = useRentalMarket()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<PriceEvolutionByCategory>({})

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)

      try {
        // Determine location type and value from filters
        let locationType = undefined
        let locationValue = undefined

        if (filters.location) {
          switch (filters.locationType) {
            case 'postal_code':
              locationType = 'postal_code_name'
              break
            case 'city':
              locationType = 'municipality_name'
              break
            case 'district':
              locationType = 'district_name'
              break
          }
          locationValue = filters.location
        }

        const apiFilters = {
          deal_type: 'rent', // Changed from 'purchase' to 'rent'
          property_category:
            filters.propertyCategory.length > 0
              ? filters.propertyCategory
              : undefined,
          location: locationValue,
          location_type: locationType,
          outlier_field: 'price',
          outlier_percentage: 2,
        }

        const response = await service.getRentalPriceEvolutionData(apiFilters)

        // Pass the raw aggregations data directly to the component
        setData(response.aggregations)
      } catch (err) {
        console.error('Error fetching rental price evolution data:', err)
        setError(t('market.common.errors.loadingData'))
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [filters, priceMode, service, t])

  return (
    <PriceEvolutionChart
      data={data}
      loading={loading}
      error={error}
      isRental={true} // Changed to true for rental
      priceMode={priceMode}
      showKPIs={false}
      showInsights={false}
      showControls={true}
      showQuantileChart={false}
      t={t}
    />
  )
}
